'use strict';

import * as queryString from 'query-string';

const SEGMENT = Symbol('segment');
const $Q = Symbol('$q');
const SEGMENT_CONFIG = Symbol('segmentConfig');
const $TIMEOUT = Symbol('$timeout');
const $WINDOW = Symbol('$window');
const QS = Symbol('queryString');
const UTM_PARAMS_NAMES = Symbol('utmParamsNames');

class AnalyticsService {
  /* eslint-disable no-unused-vars */
  static factory(segment, $q, segmentConfig, $timeout, $window) {
    'ngInject';

    return new AnalyticsService(...arguments);
  }

  constructor(segment, $q, segmentConfig, $timeout, $window) {
    this[SEGMENT] = segment;
    this[$Q] = $q;
    this[SEGMENT_CONFIG] = segmentConfig;
    this[$TIMEOUT] = $timeout;
    this[$WINDOW] = $window;
    this[QS] = queryString.parse($window.location.search);

    this[UTM_PARAMS_NAMES] = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term'];

    this.getUtmParamsFromCookies = this.getUtmParamsFromCookies.bind(this);
    this.getUtmParamsFromQueryString = this.getUtmParamsFromQueryString.bind(this);
  }

  // To create cookies
  createCookie(name, value, exDays) {
    const date = new Date();
    date.setTime(date.getTime() + (exDays * 24 * 60 * 60 * 1000));
    const expires = `; expires= ${date.toUTCString()}`;
    document.cookie = `${name} = ${value} ${expires} ; path=/`; // eslint-disable-line
  }

  readCookie(name) {
    const nameEQ = `${name}=`;
    const ca = document.cookie.split(';'); // eslint-disable-line
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  eraseCookie(name) {
    this.createCookie(name, '', -1);
  }

  getUtmParamsFromCookies(eraseCookiesAfteRead = false) {
    const paramsDetected = {};

    this[UTM_PARAMS_NAMES].forEach(paramName => {
      const value = this.readCookie(paramName);
      if (value !== null) {
        paramsDetected[paramName] = value;

        if (eraseCookiesAfteRead) {
          this.eraseCookie(paramName);
        }
      }
    });

    return paramsDetected;
  }

  getUtmParamsFromQueryString() {
    const paramsDetected = {};

    if (angular.isObject(this[QS]) && Object.keys(this[QS]).length > 0) {
      angular.forEach(this[QS], (value, key) => {
        key = key.toLowerCase().replace(/[^a-z_]+/g, '');

        if (this[UTM_PARAMS_NAMES].indexOf(key) > -1) {
          paramsDetected[key] = `${value}`.substr(0, 100).replace(/[^a-z0-9_-]+/gi, '');
        }
      });
    }

    return paramsDetected;
  }

  /**
   * Facebook Pixel
   * STAFF-4635
   */
  facebookPixelCodeTrack(event = '') {
    if (angular.isDefined(this[$WINDOW].fbq)) {
      /**
       * @since STAFF-4823 'Lead' (previously 'Registered')
       */
      this[$WINDOW].fbq('track', event);
    }
  }

  /**
   * @see https://developers.google.com/analytics/devguides/collection/analyticsjs/pages
   * STAFF-4744
   */
  googleAnalyticsPageview(pageData = {}) {
    if (angular.isDefined(this[$WINDOW].ga)) {
      if (Object.keys(pageData).length > 0) {
        this[$WINDOW].ga('set', pageData);
      }
      this[$WINDOW].ga('send', 'pageview');
    }
  }

  segmentEnabled(email = '') {
    if (!this[SEGMENT_CONFIG].apiKey) {
      // segment disabled
      return false;
    }

    /**
     * if we are here, then  segment is enabled
     * but let's filter out some emails
     *
     * @see STAFF-1419
     */

    if (!email) {
      // user is not logged in - let's allow
      return true;
    }

    // if we are here, then user is authenticated
    if (!/^[a-zA-Z0-9._+-]+@(staff|timedoctor)\.(dev|com)$/i.test(email)) {
      // email is not @staff.com nor @staff.dev - let's allow
      return true;
    }

    // email belongs to @staff.com or @staff.dev
    // so we can allow it only if contains intercom
    return /^[a-zA-Z0-9._+-]*intercom[a-zA-Z0-9._+-]*@(staff|timedoctor)\.(dev|com)$/i.test(email);
  }

  page(title, traits = {}) {
    if (this.segmentEnabled()) {
      this[SEGMENT].page(title, traits);
    }

    this.googleAnalyticsPageview();
  }

  trackEvent(
    eventName,
    properties = {
      userId: '',
      userName: '',
      userEmail: '',
      companyId: '',
      companyName: '',
      timezone: '',
      trackingMode: '',
      lastTrack: '',
      role: 'owner',
    }
  ) {
    const traits = {};

    traits.userId = properties.userId;

    if (properties.userEmail) {
      traits.email = `${properties.userEmail}`;
    }
    if (properties.userName) {
      traits.name = `${properties.userName}`;
    }

    if (properties.companyId) {
      traits.companyId = `${properties.companyId}`;
    }

    if (properties.trackingMode) {
      traits.workspace_tracking_type = `${properties.trackingMode}`;
    } else {
      traits.workspace_tracking_type = 'not selected';
    }

    if (properties.lastTrack && properties.timezone) {
      traits.last_tracked_time = new Date(properties.lastTrack).toISOString();
    }

    // add utm_* params if any detected
    let utmParams = this.getUtmParamsFromQueryString();
    if (Object.keys(utmParams).length < 1) {
      utmParams = this.getUtmParamsFromCookies(false);
    }
    angular.forEach(utmParams, (value, name) => {
      traits[name] = value;
    });

    // defaults for now
    traits.number_of_companies = 1;
    // traits.access_level = 'owner';

    if (['owner', 'admin', 'manager'].indexOf(properties.role) === -1) {
      traits.access_level = 'regular user';
    } else {
      traits.access_level = properties.role;
    }

    if (properties.timezone && properties.companyId && properties.companyName) {
      const companyCreatedAt = new Date();

      traits.timezone = properties.timezone;

      traits.companyId = properties.companyId;
      traits.companyName = properties.companyName;
      traits.companyCreatedAt = companyCreatedAt.toISOString();
    }

    // @see https://segment.com/docs/integrations/intercom/#from-the-browser
    // @see https://segment.com/docs/libraries/analytics.js/
    const deferredIdentify = this[$Q].defer();
    const deferredTrack = this[$Q].defer();

    // exit if segment enabled...
    if (!this.segmentEnabled(properties.userEmail)) {
      deferredIdentify.resolve();
      deferredTrack.resolve();

      return this[$Q].all([
        deferredIdentify.promise,
        deferredTrack.promise,
      ]);
    }

    // ensure promise resolved - wait max 5s!
    let timedOutIdentify = false;
    const promiseTimeoutDeferredIdentify = this[$TIMEOUT](
      () => {
        deferredIdentify.resolve();
        timedOutIdentify = true;
      },
      2500
    );

    let timedOutTrack = false;
    const promiseTimeoutDeferredTrack = this[$TIMEOUT](
      () => {
        deferredTrack.resolve();
        timedOutTrack = true;
      },
      2500
    );

    if (properties.userId) {
      // angular.copy sometimes does not work
      // so let's just copy object with foreach
      const identifyTraits = {};
      angular.forEach(traits, (value, key) => {
        identifyTraits[key] = value;
      });
      identifyTraits.company = {
        id: '',
        Time_Zone: '',
      };

      if (angular.isDefined(traits.companyId)) {
        identifyTraits.company.id = traits.companyId;
      }
      if (angular.isDefined(traits.timezone)) {
        identifyTraits.company.Time_Zone = traits.timezone;
      }

      this[SEGMENT].identify(
        properties.userId,
        identifyTraits,
        () => {
          if (!timedOutIdentify) {
            this[$TIMEOUT].cancel(promiseTimeoutDeferredIdentify);
            deferredIdentify.resolve();
          }
        }
      );
    } else {
      deferredIdentify.resolve();
    }

    this[SEGMENT].track(
      eventName,
      traits,
      () => {
        if (!timedOutTrack) {
          this[$TIMEOUT].cancel(promiseTimeoutDeferredTrack);
          deferredTrack.resolve();
        }
      }
    );

    return this[$Q].all([
      deferredIdentify.promise,
      deferredTrack.promise,
    ]);
  }

  trackCustomEvent(eventName, props = {}) {
    if (this.segmentEnabled()) {
      this[SEGMENT].track(eventName, props);
    }
  }
}

export default AnalyticsService;
